import React, { useRef, useEffect } from "react";
import { GLOBE_STEP, GLOBE_LAND, GLOBE_KSA } from "./globeLand";

// ─────────────────────────────────────────────────────────────────────────
// RITIS · Presentation globe
// Dotted globe (canvas, no libraries). Starts far out, rotates and zooms onto the Kingdom,
// then arcs shoot from the Al Khobar head office to every project site.
// Land data: ./globeLand.js (Natural Earth, run-length encoded).
// ─────────────────────────────────────────────────────────────────────────

const RAD = Math.PI / 180;
const HQ = { lat: 26.2794, lng: 50.2083 };
const TARGET = { lat: 22.6, lng: 45.2 };     // camera rests here (Kingdom-wide view)
const CYAN = "0,212,255";

const clamp01 = v => Math.max(0, Math.min(1, v));
const lerp = (a, b, t) => a + (b - a) * t;
const easeInOut = t => (t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeOut = t => 1 - Math.pow(1 - t, 3);
const vec = (lat, lng) => { const c = Math.cos(lat * RAD); return [c * Math.cos(lng * RAD), c * Math.sin(lng * RAD), Math.sin(lat * RAD)]; };

// ── one-time decoding of the land mask into unit vectors ──────────────────
let LAND = null;
function landPoints() {
    if (LAND) return LAND;
    const runs = s => s.split(" ").map(x => parseInt(x, 36));
    const expand = r => {
        let total = 0; r.forEach(n => { total += n; });
        const a = new Uint8Array(total); let p = 0, v = 0;
        r.forEach(n => { if (v) a.fill(1, p, p + n); p += n; v ^= 1; });
        return a;
    };
    const lm = expand(runs(GLOBE_LAND)), km = expand(runs(GLOBE_KSA));
    const rows = Math.round(180 / GLOBE_STEP);
    const X = [], Y = [], Z = [], K = [];
    let idx = 0;
    for (let i = 0; i < rows; i++) {
        const lat = 90 - GLOBE_STEP * (i + .5);
        const n = Math.max(1, Math.round(360 * Math.cos(lat * RAD) / GLOBE_STEP));
        for (let j = 0; j < n; j++, idx++) {
            if (!lm[idx]) continue;
            const v = vec(lat, -180 + 360 * (j + .5) / n);
            X.push(v[0]); Y.push(v[1]); Z.push(v[2]); K.push(km[idx]);
        }
    }
    LAND = { x: Float32Array.from(X), y: Float32Array.from(Y), z: Float32Array.from(Z), k: Uint8Array.from(K), n: X.length };
    return LAND;
}

// graticule polylines (15° grid)
let GRID = null;
function graticule() {
    if (GRID) return GRID;
    const lines = [];
    for (let lat = -75; lat <= 75; lat += 15) { const l = []; for (let lng = -180; lng <= 180; lng += 3) l.push(vec(lat, lng)); lines.push(l); }
    for (let lng = -180; lng < 180; lng += 15) { const l = []; for (let lat = -90; lat <= 90; lat += 3) l.push(vec(lat, lng)); lines.push(l); }
    GRID = lines;
    return GRID;
}

const bez = (p0, p1, p2, t) => { const u = 1 - t; return { x: u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x, y: u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y }; };
const rrect = (ctx, x, y, w, h, r) => { ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r); ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath(); };

// ── timeline (seconds): a long spin from the Atlantic to Arabia, then the network lights up ──
const START = { lat: 14, lng: -82 };
const ZOOM_AT = .8, ZOOM_LEN = 9.4;                 // rotate + zoom runs .8 → 10.2 s
const T_HQ = 6.2, T_ARCS = 7.6, T_CARD = 8.8, T_PULSE = 9.6, T_LABELS = 11.5;

/**
 * sites: [{ id, lat, lng, color, value, label }]
 */
export default function PresentationGlobe({ active, sites = [], logo, showHQCard = true }) {
    const cvRef = useRef(null);
    const sitesRef = useRef(sites);
    sitesRef.current = sites;
    const logoRef = useRef(null);
    // showHQCard: بطاقة "RITIS OPERATIONS / Al Khobar · Head Office" اللي بتظهر
    // جنب نقطة الخبر على الكرة — true (الأصل) في مانجر برزنتيشن، وبتتبعت false
    // من صفحات تسجيل الدخول بناءً على طلب باكري (الكرة من غير البطاقة دي).
    const showHQCardRef = useRef(showHQCard);
    showHQCardRef.current = showHQCard;

    useEffect(() => {
        if (!logo) return;
        const im = new Image();
        im.onload = () => { logoRef.current = im; };
        im.src = logo;
    }, [logo]);

    useEffect(() => {
        const cv = cvRef.current;
        if (!cv || !active) return;
        const ctx = cv.getContext("2d");
        const land = landPoints(), grid = graticule();
        const reduce = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const skip = reduce ? 30 : 0;
        const hqv = vec(HQ.lat, HQ.lng);
        let W = 0, H = 0, dpr = 1, raf = 0;
        const t0 = performance.now();

        const resize = () => {
            const r = cv.getBoundingClientRect();
            dpr = Math.min(window.devicePixelRatio || 1, 2);
            W = Math.max(1, r.width); H = Math.max(1, r.height);
            cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
        };
        resize();
        const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
        ro?.observe(cv);
        window.addEventListener("resize", resize);

        // unique arc targets (identical coordinates collapse to one node) with a fixed launch order
        const buildSites = () => {
            const seen = new Set();
            return [...sitesRef.current]
                .filter(s => Number.isFinite(s.lat) && Number.isFinite(s.lng))
                .sort((a, b) => (b.value || 0) - (a.value || 0))
                .filter(s => { const k = `${s.lat.toFixed(3)},${s.lng.toFixed(3)}`; if (seen.has(k)) return false; seen.add(k); return true; })
                .map(s => ({ ...s, v: vec(s.lat, s.lng) }));
        };
        let nodes = buildSites();
        let nodesN = sitesRef.current.length;

        const draw = now => {
            raf = requestAnimationFrame(draw);
            if (sitesRef.current.length !== nodesN) { nodes = buildSites(); nodesN = sitesRef.current.length; }
            const t = (now - t0) / 1000 + skip;
            const narrow = W < 900;
            const cx = narrow ? W * .5 : W * .67, cy = narrow ? H * .245 : H * .5;
            const Rfar = narrow ? Math.min(W * .40, H * .2) : H * .40;
            const Rnear = narrow ? W * 2.4 : H * 1.6;

            // camera: rotate + exponential zoom, then a very slow drift
            const zt = easeInOut(clamp01((t - ZOOM_AT) / ZOOM_LEN));
            const R = Rfar * Math.pow(Rnear / Rfar, zt);
            const settle = Math.max(0, t - (ZOOM_AT + ZOOM_LEN));
            const lam0 = lerp(START.lng, TARGET.lng, zt) + Math.sin(settle * .16) * 1.3;
            const phi0 = lerp(START.lat, TARGET.lat, zt) + Math.sin(settle * .12) * .5;
            const cl = Math.cos(phi0 * RAD), sl = Math.sin(phi0 * RAD), cL = Math.cos(lam0 * RAD), sL = Math.sin(lam0 * RAD);
            const fx = cl * cL, fy = cl * sL, fz = sl;      // towards the viewer
            const ex = -sL, ey = cL;                        // east
            const nx = -sl * cL, ny = -sl * sL, nz = cl;    // north
            const scr = v => ({ x: cx + R * (v[0] * ex + v[1] * ey), y: cy - R * (v[0] * nx + v[1] * ny + v[2] * nz), z: v[0] * fx + v[1] * fy + v[2] * fz });

            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.clearRect(0, 0, W, H);

            // sphere body
            if (R < Math.max(W, H) * 6) {
                const g = ctx.createRadialGradient(cx - R * .25, cy - R * .3, R * .05, cx, cy, R);
                g.addColorStop(0, "#0e2140"); g.addColorStop(.55, "#0a1730"); g.addColorStop(1, "#050a16");
                ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, R, 0, 6.2832); ctx.fill();
                // atmosphere rim
                const a = ctx.createRadialGradient(cx, cy, R * .97, cx, cy, R * 1.09);
                a.addColorStop(0, `rgba(${CYAN},0)`); a.addColorStop(.22, `rgba(${CYAN},.28)`); a.addColorStop(1, `rgba(${CYAN},0)`);
                ctx.fillStyle = a; ctx.beginPath(); ctx.arc(cx, cy, R * 1.09, 0, 6.2832); ctx.fill();
            }

            // graticule
            ctx.lineWidth = 1; ctx.strokeStyle = "rgba(96,165,250,.10)";
            ctx.beginPath();
            grid.forEach(line => {
                let pen = false;
                for (let i = 0; i < line.length; i++) {
                    const p = scr(line[i]);
                    if (p.z <= 0) { pen = false; continue; }
                    if (pen) ctx.lineTo(p.x, p.y); else { ctx.moveTo(p.x, p.y); pen = true; }
                }
            });
            ctx.stroke();

            // land dots — 3 depth buckets for the world, 3 for the Kingdom, 1 for the radar pulse from HQ
            const paths = [new Path2D(), new Path2D(), new Path2D(), new Path2D(), new Path2D(), new Path2D(), new Path2D()];
            const dia = Math.max(1.1, Math.min(4.6, R * .0044));
            const round = dia > 2.2, half = dia / 2;
            const pulseOn = t > T_PULSE;
            const wa = pulseOn ? (((t - T_PULSE) * .2) % 1) * .62 : -1, ww = .02;   // radians from HQ
            const lo = Math.cos(wa + ww), hi = Math.cos(Math.max(0, wa - ww));
            const { x: LX, y: LY, z: LZ, k: LK, n } = land;
            for (let i = 0; i < n; i++) {
                const X = LX[i], Y = LY[i], Z = LZ[i];
                const dz = X * fx + Y * fy + Z * fz;
                if (dz <= 0.02) continue;
                const sx = cx + R * (X * ex + Y * ey), sy = cy - R * (X * nx + Y * ny + Z * nz);
                if (sx < -8 || sx > W + 8 || sy < -8 || sy > H + 8) continue;
                let b;
                if (pulseOn) { const d = X * hqv[0] + Y * hqv[1] + Z * hqv[2]; if (d >= lo && d <= hi) b = 6; }
                if (b === undefined) b = (LK[i] ? 3 : 0) + (dz < .35 ? 0 : dz < .7 ? 1 : 2);
                const P = paths[b], r = LK[i] && b !== 6 ? half * 1.2 : half;
                if (round) { P.moveTo(sx + r, sy); P.arc(sx, sy, r, 0, 6.2832); } else P.rect(sx - r, sy - r, r * 2, r * 2);
            }
            const fills = ["rgba(96,165,250,.16)", "rgba(96,165,250,.34)", "rgba(125,180,255,.6)", `rgba(${CYAN},.35)`, `rgba(${CYAN},.7)`, `rgba(120,235,255,1)`, "rgba(190,245,255,.9)"];
            paths.forEach((P, i) => { ctx.fillStyle = fills[i]; ctx.fill(P); });

            // ── HQ + arcs ────────────────────────────────────────────────
            const hq = scr(hqv);
            const hqAlpha = clamp01((t - T_HQ) / 1.4);
            const arcStart = T_ARCS, stepT = .24, growT = 1.8;
            const vis = [];
            if (hq.z > .05 && hqAlpha > 0) {
                nodes.forEach((s, i) => {
                    const p = scr(s.v);
                    if (p.z <= .05) return;
                    const dHq = Math.hypot(p.x - hq.x, p.y - hq.y);
                    const prog = clamp01((t - arcStart - i * stepT) / growT);
                    vis.push({ s, p, i, prog, dHq });
                });
                // arcs
                vis.forEach(({ s, p, i, prog, dHq }) => {
                    if (prog <= 0 || dHq < 14) return;
                    const mx = (hq.x + p.x) / 2, my = (hq.y + p.y) / 2;
                    let px = -(p.y - hq.y), py = p.x - hq.x;
                    const pl = Math.hypot(px, py) || 1; px /= pl; py /= pl;
                    if (py > 0) { px = -px; py = -py; }
                    const lift = dHq * .3;
                    const c = { x: mx + px * lift, y: my + py * lift };
                    const e = easeOut(prog), N = 44, upto = Math.max(2, Math.round(N * e));
                    ctx.save();
                    ctx.lineCap = "round"; ctx.shadowColor = `rgba(${CYAN},.9)`; ctx.shadowBlur = 8;
                    ctx.strokeStyle = `rgba(${CYAN},.55)`; ctx.lineWidth = 1.5;
                    ctx.beginPath();
                    for (let k = 0; k <= upto; k++) { const q = bez(hq, c, p, k / N); if (k) ctx.lineTo(q.x, q.y); else ctx.moveTo(q.x, q.y); }
                    ctx.stroke();
                    // traveling pulse once the arc is complete
                    if (prog >= 1) {
                        const ph = ((t * .32 + i * .173) % 1);
                        const head = bez(hq, c, p, ph), tail = bez(hq, c, p, Math.max(0, ph - .1));
                        ctx.shadowBlur = 14; ctx.strokeStyle = "rgba(200,245,255,.95)"; ctx.lineWidth = 2.2;
                        ctx.beginPath(); ctx.moveTo(tail.x, tail.y); ctx.lineTo(head.x, head.y); ctx.stroke();
                    }
                    ctx.restore();
                });
                // site nodes
                vis.forEach(({ s, p, i, prog }) => {
                    if (prog < .85) return;
                    const a = clamp01((prog - .85) / .15);
                    const ph = (t * .7 + i * .137) % 1;
                    ctx.save(); ctx.globalAlpha = a;
                    ctx.strokeStyle = s.color || `rgb(${CYAN})`; ctx.lineWidth = 1.5; ctx.globalAlpha = a * (1 - ph) * .8;
                    ctx.beginPath(); ctx.arc(p.x, p.y, 5 + ph * 15, 0, 6.2832); ctx.stroke();
                    ctx.globalAlpha = a; ctx.shadowColor = s.color || `rgb(${CYAN})`; ctx.shadowBlur = 12;
                    ctx.fillStyle = s.color || `rgb(${CYAN})`; ctx.beginPath(); ctx.arc(p.x, p.y, 4.2, 0, 6.2832); ctx.fill();
                    ctx.shadowBlur = 0; ctx.strokeStyle = "#fff"; ctx.lineWidth = 1.6; ctx.stroke();
                    ctx.restore();
                });

                // HQ: radar rings + node
                ctx.save(); ctx.globalAlpha = hqAlpha;
                for (let k = 0; k < 3; k++) {
                    const ph = ((t * .45 + k / 3) % 1);
                    ctx.globalAlpha = hqAlpha * (1 - ph) * .7; ctx.strokeStyle = `rgba(${CYAN},1)`; ctx.lineWidth = 2;
                    ctx.beginPath(); ctx.arc(hq.x, hq.y, 8 + ph * Math.min(90, H * .11), 0, 6.2832); ctx.stroke();
                }
                ctx.globalAlpha = hqAlpha; ctx.shadowColor = `rgba(${CYAN},1)`; ctx.shadowBlur = 22;
                ctx.fillStyle = "#e0faff"; ctx.beginPath(); ctx.arc(hq.x, hq.y, 6.5, 0, 6.2832); ctx.fill();
                ctx.shadowBlur = 0; ctx.strokeStyle = `rgba(${CYAN},1)`; ctx.lineWidth = 2.5; ctx.beginPath(); ctx.arc(hq.x, hq.y, 11, 0, 6.2832); ctx.stroke();
                ctx.restore();

                // HQ label card (top-right of the node, over the Gulf where there are no arcs)
                // — متخفية بالكامل لو showHQCard=false (صفحات تسجيل الدخول)
                const cardA = showHQCardRef.current ? clamp01((t - T_CARD) / .9) : 0;
                const placed = [];
                if (cardA > 0) {
                    const fs = narrow ? 11 : 13;
                    const img = logoRef.current;
                    const logoH = narrow ? 18 : 24, logoW = img ? Math.min(logoH * (img.width / img.height), 70) : 0;
                    ctx.font = `800 ${fs}px 'Roboto',system-ui,sans-serif`;
                    const t1 = "RITIS OPERATIONS";
                    const w1 = ctx.measureText(t1).width;
                    ctx.font = `600 ${fs - 2.5}px 'Roboto',system-ui,sans-serif`;
                    const t2 = "Al Khobar · Head Office";
                    const w2 = ctx.measureText(t2).width;
                    const pad = 12, cw = Math.max(w1, w2) + pad * 2 + (logoW ? logoW + 10 : 0), ch = narrow ? 42 : 50;
                    let bx = hq.x + (narrow ? 22 : 56), by = hq.y - ch - (narrow ? 14 : 30);
                    if (bx + cw > W - 10) bx = W - 10 - cw;
                    if (by < 70) by = 70;
                    ctx.save(); ctx.globalAlpha = cardA * hqAlpha;
                    ctx.strokeStyle = `rgba(${CYAN},.6)`; ctx.lineWidth = 1.2;
                    ctx.beginPath(); ctx.moveTo(hq.x + 8, hq.y - 8); ctx.lineTo(bx, by + ch); ctx.stroke();
                    ctx.shadowColor = "rgba(0,0,0,.6)"; ctx.shadowBlur = 24;
                    rrect(ctx, bx, by, cw, ch, 12); ctx.fillStyle = "rgba(6,12,24,.9)"; ctx.fill();
                    ctx.shadowBlur = 0; ctx.strokeStyle = `rgba(${CYAN},.55)`; ctx.stroke();
                    let tx = bx + pad;
                    if (img && logoW) { ctx.drawImage(img, tx, by + (ch - logoH) / 2, logoW, logoH); tx += logoW + 10; }
                    ctx.textBaseline = "alphabetic"; ctx.textAlign = "left";
                    ctx.fillStyle = "#fff"; ctx.font = `800 ${fs}px 'Roboto',system-ui,sans-serif`; ctx.fillText(t1, tx, by + ch / 2 - 2);
                    ctx.fillStyle = "#7dd3fc"; ctx.font = `600 ${fs - 2.5}px 'Roboto',system-ui,sans-serif`; ctx.fillText(t2, tx, by + ch / 2 + fs - 1);
                    ctx.restore();
                    placed.push({ x0: bx - 4, y0: by - 4, x1: bx + cw + 4, y1: by + ch + 4 });
                }
                placed.push({ x0: hq.x - 16, y0: hq.y - 16, x1: hq.x + 16, y1: hq.y + 16 });
                vis.forEach(v => placed.push({ x0: v.p.x - 8, y0: v.p.y - 8, x1: v.p.x + 8, y1: v.p.y + 8 }));

                // site labels (largest contracts first, skipped when they would collide)
                const labA = clamp01((t - T_LABELS) / .9);
                if (labA > 0) {
                    const fs = narrow ? 9.5 : 11, maxL = narrow ? 5 : 9;
                    ctx.font = `700 ${fs}px 'Roboto',system-ui,sans-serif`;
                    ctx.textAlign = "center"; ctx.textBaseline = "middle";
                    let shown = 0;
                    const hit = (a, b) => a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;
                    vis.filter(v => v.prog >= 1 && v.s.label).forEach(({ s, p }) => {
                        if (shown >= maxL) return;
                        const w = ctx.measureText(s.label).width + 16, h = fs + 10;
                        const opts = [[0, -18 - h / 2], [0, 18 + h / 2], [w / 2 + 10, 0], [-(w / 2 + 10), 0]];
                        for (const [ox, oy] of opts) {
                            const r = { x0: p.x + ox - w / 2, y0: p.y + oy - h / 2, x1: p.x + ox + w / 2, y1: p.y + oy + h / 2 };
                            if (r.x0 < 8 || r.x1 > W - 8 || r.y0 < 66 || r.y1 > H - 60) continue;
                            if (placed.some(q => hit(r, q))) continue;
                            placed.push(r); shown++;
                            ctx.save(); ctx.globalAlpha = labA;
                            rrect(ctx, r.x0, r.y0, w, h, h / 2); ctx.fillStyle = "rgba(6,12,24,.82)"; ctx.fill();
                            ctx.strokeStyle = "rgba(255,255,255,.22)"; ctx.lineWidth = 1; ctx.stroke();
                            ctx.fillStyle = "#e2e8f0"; ctx.fillText(s.label, p.x + ox, p.y + oy + .5);
                            ctx.restore();
                            break;
                        }
                    });
                }
            }
        };
        raf = requestAnimationFrame(draw);
        return () => { cancelAnimationFrame(raf); ro?.disconnect(); window.removeEventListener("resize", resize); };
    }, [active]);

    return <canvas ref={cvRef} aria-hidden="true" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }} />;
}
